import React, { lazy, Suspense } from "react";

import AttendanceManagementImg from "@/assets/AttendenceManagement/waggex-why-switch-attendance-management-india.webp?w=380;520&format=webp";

import AttendanceManagementmobileImg from "@/assets/AttendenceManagement/waggex-attendance-control-visibility-shift-payroll-india600.webp?w=380&format=webp";

import AttendanceManagement1 from "@/assets/AttendenceManagement/waggex-attendance-control-visibility-shift-payroll-india.webp?w=1024&format=webp";

import { AlertTriangle, UserX, Map, IndianRupee } from "lucide-react";
import StatsSection from "../components/common/StatsSection";
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("./HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));

const DynamicFeaturesSection = lazy(
  () => import("../components/common/DynamicFeaturesSection"),
);

const attendanceMangFeatures = [
  {
    id: 1,
    title: "Daily Attendance Tracking",
    desc: "Monitor employee check-ins, late arrivals, absences, and working hours from one centralized attendance dashboard.",
  },
  {
    id: 2,
    title: "Selfie Check-In",
    desc: "Employees can mark attendance securely using selfie verification directly from the Waggex Staff App.",
  },
  {
    id: 3,
    title: "Location Verification",
    desc: "Capture attendance with live GPS location data for office staff, remote teams, and field employees.",
  },
  {
    id: 4,
    title: "Shift-Based Attendance",
    desc: "Manage attendance according to assigned shifts, weekly offs, and custom work schedules automatically.",
  },
  {
    id: 5,
    title: "Attendance Reports",
    desc: "Generate detailed attendance summaries, late reports, overtime records, and workforce insights instantly.",
  },
  {
    id: 6,
    title: "Employee Attendance History",
    desc: "Maintain organized employee attendance records with complete check-in and work history visibility.",
  },
  {
    id: 7,
    title: "Field Staff Attendance",
    desc: "Track attendance for sales teams, service staff, and field employees working across multiple locations.",
  },
  {
    id: 8,
    title: "AI Face Attendance",
    desc: "Use AI-powered face recognition attendance to prevent fake check-ins and improve attendance accuracy.",
  },
  {
    id: 9,
    title: "Payroll-Ready Data",
    desc: "Sync verified attendance records directly with payroll processing for accurate salary calculations.",
  },
];
const rows = [
  {
    feature: "Employee Check-In",
    without: "Register or paper entry",
    with: "Mobile app, GPS & face recognition attendance",
  },
  {
    feature: "Shift Tracking",
    without: "Manual updates by HR",
    with: "Automated shift rules & real-time alerts",
  },
  {
    feature: "Attendance Reports",
    without: "Delayed, compiled manually",
    with: "Instant live reports on demand",
  },
  {
    feature: "Leave Management",
    without: "Separate records, no system sync",
    with: "Fully integrated with attendance system",
  },
  {
    feature: "Payroll Preparation",
    without: "Manual corrections every month",
    with: "Payroll-ready data automatically synced",
  },
  {
    feature: "Remote Team Tracking",
    without: "Calls and manual reporting",
    with: "Real-time GPS-based visibility",
  },
  {
    feature: "Attendance Accuracy",
    without: "Error-prone and often disputed",
    with: "Verified, automated, consistent records",
  },
  {
    feature: "HR Monitoring",
    without: "Limited visibility, reactive control",
    with: "Centralized real-time HR dashboard",
  },
];
const headers = [
  "ATTENDANCE TASK",
  "TRADITIONAL PROCESS",
  "WAGGEX ATTENDANCE SOFTWARE",
];
const attendanceMangFaqs1 = [
  {
    title: "What is Waggex Attendance Management Software?",
    content:
      "Waggex is a cloud-based attendance management system that helps Indian businesses manage employee attendance, shifts, work hours, overtime, and leave records digitally and connects all of it to payroll automatically. It replaces manual registers, spreadsheets, and WhatsApp-based attendance updates with a single verified system",
  },
  {
    title: "Can employees mark attendance from mobile phones?",
    content:
      "Yes. Employees use the Waggex Staff App to mark attendance from anywhere. The app supports GPS location stamps, selfie verification, and face recognition check-ins. Every entry is verified, timestamped, and synced to the HR dashboard in real time",
  },
  {
    title: "Does the software support field staff attendance?",
    content:
      "Yes. Waggex supports GPS attendance tracking for field employees and remote teams. Every check-in captures the employee's real-time location, making it easy to verify field attendance without manual calls or reports. Records feed directly into payroll at the end of the month",
  },
];
const attendanceMangFaqs2 = [
  {
    title: "Can attendance records sync with payroll?",
    content:
      "Yes. Attendance data integrates directly with Waggex payroll calculations. Working days, approved leave, LOP deductions, and overtime are all applied automatically at the point of payroll processing — no manual entry and no spreadsheet reconciliation",
  },
  {
    title: "Is face recognition attendance available?",
    content:
      "Yes. Waggex supports AI face recognition attendance through FaceLens, which verifies employee identity at the point of check-in. It eliminates proxy entries and buddy punching completely and works for office, remote, and field employees",
  },
  {
    title: "Is the software suitable for growing businesses?",
    content:
      "Yes. Waggex attendance software is built for startups, growing SMEs, and large multi-branch organisations across India. Businesses from 10 to 1,000 employees get GPS tracking, shift automation, leave sync, and payroll-ready attendance with no hardware or IT team required",
  },
];

const schema = [
  // SCHEMA 1: SoftwareApplication
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Attendance Management System",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS",
    description:
      "Waggex Attendance Management Software automates employee check-ins, GPS tracking, shift management, face recognition, leave sync, and payroll-ready attendance reporting for Indian businesses",
    url: "https://www.waggex.com/attendance-management",
    featureList: [
      "Attendance management system India",
      "GPS attendance tracking software",
      "AI face recognition attendance India",
      "Field staff attendance tracking",
      "Automated shift tracking",
      "Leave and attendance sync",
      "Payroll-ready attendance data",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "287",
      bestRating: "5",
      worstRating: "1",
    },
  },

  // SCHEMA 2: FAQPage
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is Waggex Attendance Management Software?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Waggex is a cloud-based attendance management system that helps Indian businesses manage employee attendance, shifts, work hours, overtime, and leave records digitally connected to payroll automatically.",
        },
      },
      {
        "@type": "Question",
        name: "Can employees mark attendance from mobile phones?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Employees use the Waggex Staff App with GPS location, selfie verification, and AI face recognition check-ins, synced in real time to HR dashboards.",
        },
      },
      {
        "@type": "Question",
        name: "Does the software support field staff attendance?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex supports GPS-based attendance tracking for field employees and remote teams, with real-time location verification.",
        },
      },
      {
        "@type": "Question",
        name: "Can attendance records sync with payroll?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Working days, leave, deductions, and overtime are automatically calculated and synced with payroll without manual entry.",
        },
      },
      {
        "@type": "Question",
        name: "Is face recognition attendance available?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex includes AI face recognition via FaceLens to prevent proxy attendance and buddy punching.",
        },
      },
      {
        "@type": "Question",
        name: "Is the software suitable for growing businesses?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Waggex is designed for businesses from 10 to 1,000+ employees with scalable HR and attendance automation tools.",
        },
      },
    ],
  },

  // SCHEMA 3: Organization
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides employee attendance management software and complete HRMS solutions for Indian businesses.",
    areaServed: "IN",
  },
];
const cards = [
  {
    title: "Manual Attendance",
    items: [
      "Useful for corrections",
      "Admin-controlled entries",
      "Simple attendance review",
    ],
  },
  {
    title: "Selfie Attendance",
    items: [
      "Employee-friendly check-in",
      "Photo-based record support",
      "Useful for remote teams",
    ],
  },
  {
    title: "Geo-Location Attendance",
    items: [
      "Location-supported records",
      "Useful for field staff",
      "Workplace check-in control",
    ],
  },
  {
    title: "FaceLens AI Attendance",
    items: [
      "Location-supported records",
      "Useful for field staff",
      "Helps reduce proxy attendance",
    ],
  },
];

const appfeatures = [
  {
    icon: <AlertTriangle size={24} aria-hidden="true" />,
    title: "Manual Entry Errors",
    desc: "Small attendance mistakes in registers or Excel can lead to major payroll and reporting issues",
  },
  {
    icon: <UserX size={24} aria-hidden="true" />,
    title: "Proxy Attendance",
    desc: "Traditional attendance systems make it easy for fake or inaccurate check-ins without verification",
  },
  {
    icon: <Map size={24} aria-hidden="true" />,
    title: "Field Staff Confusion",
    desc: "Difficult to track and verify attendance of employees working across multiple locations",
  },
  {
    icon: <IndianRupee size={24} aria-hidden="true" />,
    title: "Payroll Mismatch",
    desc: "Incorrect attendance data leads to salary errors, overtime disputes, and delayed payroll processing",
  },
];
function MobileFeatureCard({ text }) {
  return (
    <div
      className="
      w-44
      rounded-2xl
      px-2
      py-6
      flex
      items-center
      gap-6
      border
      border-white/70
      backdrop-blur-md
    "
      style={{
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(209,223,234,0.92) 100%)",
        boxShadow: `
          inset -10px -10px 20px rgba(255,255,255,0.35),
          0 10px 25px rgba(16,50,79,0.12)
        `,
      }}
    >
      <span className="text-[#10324F] text-3xl">✓</span>

      <p className="text-[#10324F] text-lg font-medium leading-snug">{text}</p>
    </div>
  );
}
function FeatureCard({ text }) {
  return (
    <div
      className="
      w-50.5
      rounded-2xl
      px-6
      py-5
      flex
      items-center
      gap-4
      border
      border-white/70
      backdrop-blur-md
    "
      style={{
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.45) 0%, rgba(209,223,234,0.92) 100%)",
        boxShadow: `
          inset -10px -10px 20px rgba(255,255,255,0.35),
          0 10px 25px rgba(16,50,79,0.12)
        `,
      }}
    >
      <span className="text-[#10324F] text-3xl">✓</span>

      <p className="text-[#10324F] text-lg font-medium leading-snug">{text}</p>
    </div>
  );
}
export default function AttendanceManagement() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      <Helmet>
        <title>Employee Attendance Management Software For India</title>
        <meta
          name="description"
          content="HR attendance management software for India with GPS attendance, shift tracking, payroll-ready reports, and employee attendance management."
        />
        <meta
          name="keywords"
          content="attendance management system,attendance management software India,employee attendance tracking software,online attendance management system India,HR attendance software India,GPS attendance tracking India,selfie attendance app India,field staff attendance tracking software,face recognition attendance software India"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/attendance-management"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>

      <section className="w-full bg-[#d3e0eb]">
        <div className="mx-auto px-4">
          {/* TOP CONTENT */}
          <div className="text-center top-10 relative">
            <h1 className="text-xl sm:text-3xl font-roboto font-bold text-[#10324F] leading-tight min-h-10">
              Attendance Management Software Indian Businesses
            </h1>

            <p className="mt-4 text-sm sm:text-base lg:text-lg text-[#10324F] max-w-3xl mx-auto">
              Track attendance, shifts, overtime, and employee work hours from
              one connected attendance management system. Built for offices,
              remote teams, and field staff across India all on a single
              platform that updates in real time.
            </p>

            {/* BUTTONS */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-8">
              <button className="w-full sm:w-auto px-8 py-3 rounded-xl bg-[#10324F] text-white font-semibold">
                Start Free Today
              </button>

              <button
                onClick={() => (window.location.href = "/contact-us")}
                className="w-full sm:w-auto px-8 py-3 rounded-xl border border-[#10324F] text-[#10324F] mb-4 sm:m-0"
              >
                Book Free Demo
              </button>
            </div>
          </div>

          {/* BOTTOM IMAGE SECTION */}
          <div className="-mt-15 flex justify-center">
            <div className="relative w-full sm:max-w-5xl sm:mx-auto mt-30 sm:mt-0">
              <picture>
                <source
                  media="(max-width: 639px)"
                  srcSet={AttendanceManagementmobileImg}
                />
                <img
                  src={AttendanceManagement1}
                  width={1024}
                  height={490}
                  fetchPriority="high"
                  loading="eager"
                  alt="Waggex employee attendance management software dashboard showing GPS check-ins shift tracking overtime and payroll-ready data for Indian businesses"
                  className="w-full h-auto object-contain"
                />
              </picture>
              {/* <img
                src={AttendanceManagementmobileImg}
                width={398}
                height={297}
                fetchPriority="high"
                alt="Waggex employee attendance management software dashboard showing GPS check-ins shift tracking overtime and payroll-ready data for Indian businesses"
                className="w-full h-auto object-contain block sm:hidden mt-26 mb-4"
              /> */}
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <section className="w-full bg-[#FFFFFF] py-10 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center relative">
            <h2 className="text-[#10324F] text-xl sm:text-3xl font-bold uppercase">
              Stop Wasting Hours Managing Employee Attendance
            </h2>

            <p className="mt-4 text-base leading-6 text-[#10324F]">
              Most businesses waste hours fixing attendance errors, managing
              shifts manually, and correcting payroll mismatches caused by
              disconnected systems. Waggex automates attendance, leave, and
              shift tracking so HR teams spend less time correcting records.
            </p>
          </div>

          {/* Cards */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-4 gap-6">
            {appfeatures.map((item, index) => (
              <div
                key={index}
                className="
                rounded-xl
                border-2 border-[#AEAEC014]
                min-h-58 sm:min-h-60
                flex flex-col
                items-start
                p-6 w-full
                backdrop-blur-[15px]
              "
                style={{
                  boxShadow: `
                  rgb(167 191 210 / 40%) -15px -15px 20px inset,
                  rgb(170 188 202 / 30%) -20px -20px 30px
                `,
                }}
              >
                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-[#10324F] flex items-center justify-center text-white shadow-sm mb-5">
                  {item.icon}
                </div>

                <h2 className="text-[#10324F] text-lg font-semibold">
                  {item.title}
                </h2>

                <p className="mt-3 text-sm text-[#6B7280]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="w-full bg-[#F6F9FB] px-6 py-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="order-1 md:order-2 space-y-4">
              {/* Left Content */}
              <div className="space-y-6 text-center lg:text-left">
                <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold leading-snug">
                  Get Complete Control Over Employee Attendance
                </h2>

                <p className="selfie-service-list-item-text py-4">
                  Waggex gives businesses a centralized attendance management
                  system where attendance records, work hours, overtime, shifts,
                  and leave data stay connected in real time. No more chasing
                  updates. No more month-end corrections
                </p>

                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="mt-4 bg-[#10324F] text-white h-12.5 px-14 rounded-xl text-xl shadow-lg hover:scale-[1.02] transition"
                >
                  Switch to Waggex Attendance
                </button>
              </div>
            </div>
            {/* Right Cards */}

            <div className="order-2 md:order-2 flex justify-center">
              <img
                src={AttendanceManagementImg[0]}
                width="382"
                height="262"
                fetchPriority="high"
                alt="Why Indian businesses switch from manual attendance systems to Waggex automated attendance management software benefits India"
                className="object-contain block sm:hidden"
              />
              <img
                src={AttendanceManagementImg[1]} // fallback image
                // srcSet={`${FacelensApp1[0]} 400w,${FacelensApp1[1]} 600w,${FacelensApp1[2]} 800w`}
                sizes="(max-width: 640px) 100vw"
                width="544"
                height="371"
                fetchPriority="high"
                alt="Why Indian businesses switch from manual attendance systems to Waggex automated attendance management software benefits India"
                className="object-contain hidden sm:block"
              />
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <DynamicFeaturesSection
          heading="Powerful Attendance Management Features"
          description="Waggex Attendance Management gives HR teams clear visibility over employee attendance, shifts, leaves, field check-ins, and payroll-ready records."
          dynamicFeatures={attendanceMangFeatures}
        />
        <section className="bg-[#F6FAFD] py-12 sm:py-16 lg:py-20 px-4">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
            {/* Left Content */}
            <div className="w-full lg:w-1/2">
              <h2 className="text-xl sm:text-3xl font-roboto font-bold text-[#10324F] leading-tight">
                Flexible Attendance Methods for Every Team
              </h2>

              <p className="mt-4 text-sm sm:text-base text-[#10324F] leading-relaxed max-w-md">
                Waggex supports multiple attendance methods based on employee
                roles, workplace setup, and daily business operations. Manage
                office staff, remote employees, and field teams with better
                attendance accuracy and control
              </p>
            </div>

            {/* Right Grid */}
            <div className="w-full lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {cards.map((card, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-5"
                  style={{
                    boxShadow: `
                  rgb(167 191 210 / 40%) -15px -15px 20px inset,
                  rgb(170 188 202 / 30%) -20px -20px 30px
                `,
                  }}
                >
                  <h2 className="text-[#10324F] font-semibold text-base mb-3">
                    {card.title}
                  </h2>

                  <ul className="space-y-2">
                    {card.items.map((item, i) => (
                      <li
                        key={i}
                        className="text-sm text-[#10324F] flex items-start gap-2"
                      >
                        <span className="text-[#10324F] mt-0.5">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
        <ComparisonTable
          headers={headers}
          rows={rows}
          title="From Manual Attendance Tracking to Automated Workforce Management"
          subtitle="See exactly how Waggex replaces every broken step of the traditional attendance process with a faster, verified, and payroll-connected workflow"
          source="payrollfeature"
        />
        <CustomerReviews />
        {/* <FAQ /> */}
        <HomeFaq
          faq1={attendanceMangFaqs1}
          faq2={attendanceMangFaqs2}
          subtitle="Common questions from Indian businesses evaluating Waggex Attendance Management Software"
        />
        <PayrollBanner
          heading="Stop Managing Payroll on Excel & Manual Sheets"
          description="Simplify payroll calculations, reduce salary errors, and process payroll faster using one centralized payroll management system"
          buttonText="Start Using Waggex Payroll"
        />
        <AppDownloadCard />
        <ModernizeHR
          heading="Simplify Payroll Processing With Waggex"
          description="Run accurate, compliant payroll every month using payroll management software built for the way Indian businesses actually operate. No spreadsheets. No errors. Start free today."
        />
      </Suspense>
    </>
  );
}
